import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Eye, Crown, Loader2, Lock } from "lucide-react";
import { collection, doc, getDoc, getDocs, query, where, orderBy, limit, documentId } from "firebase/firestore";
import SettingsLayout from "./SettingsLayout";
import useSettingsProfile from "./useSettingsProfile";
import { db } from "../../firebase";
import { isPremiumActive } from "../../utils/membership";
import { createProfilePath } from "../../utils/profileRoutes";
import { getProfileDisplayImage } from "../../utils/profilePlaceholder";
import EmptyState from "../../Components/EmptyState";

export default function ProfileViewsSettings() {
  const { user, profile, loading } = useSettingsProfile();
  const [views, setViews] = useState([]);
  const [viewProfiles, setViewProfiles] = useState({});
  const [viewsLoading, setViewsLoading] = useState(true);

  const premium = isPremiumActive(profile);

  useEffect(() => {
    if (!user || loading) return;
    const loadViews = async () => {
      try {
        const viewsSnap = await getDocs(
          query(
            collection(db, "profileViews"),
            where("viewedId", "==", user.uid),
            orderBy("viewedAt", "desc"),
            limit(50)
          )
        );
        const viewData = viewsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setViews(viewData);

        // Load viewer profiles in batches for up to 20 viewers
        if (premium && viewData.length > 0) {
          const viewerIds = [...new Set(viewData.map((v) => v.viewerId).filter(Boolean))].slice(0, 20);
          const chunks = [];
          for (let i = 0; i < viewerIds.length; i += 10) {
            chunks.push(viewerIds.slice(i, i + 10));
          }

          const profiles = {};
          for (const chunk of chunks) {
            try {
              const snap = await getDocs(query(collection(db, "profiles"), where(documentId(), "in", chunk)));
              snap.docs.forEach((docSnap) => {
                profiles[docSnap.id] = { id: docSnap.id, ...docSnap.data() };
              });
            } catch {
              // ignore individual chunk failures
            }
          }
          setViewProfiles(profiles);
        }
      } catch (err) {
        console.warn("Could not load profile views:", err.message);
      } finally {
        setViewsLoading(false);
      }
    };
    loadViews();
  }, [user, loading, premium]);

  const viewCount = views.length;

  function formatViewTime(viewedAt) {
    if (!viewedAt?.toDate) return "";
    const date = viewedAt.toDate();
    const now = new Date();
    const diff = now - date;
    if (diff < 60000) return "Just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
    return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  }

  return (
    <SettingsLayout
      title="Who viewed your profile"
      description="See who's interested in your profile."
    >
      {viewsLoading || loading ? (
        <div className="card flex justify-center p-10">
          <Loader2 className="animate-spin text-brand-600" size={28} />
        </div>
      ) : viewCount === 0 ? (
        <EmptyState
          Icon={Eye}
          title="No views yet"
          description="When someone views your profile, they'll appear here."
          action={<Link to="/matches" className="btn-primary px-5">Browse matches</Link>}
        />
      ) : (
        <>
          {/* View count card */}
          <div className="card-elevated relative overflow-hidden p-5 sm:p-6">
            <div className="pointer-events-none absolute -right-12 -top-12 h-40 w-40 rounded-full bg-brand-50 blur-3xl" />
            <div className="relative flex items-center gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-gradient text-white shadow-soft">
                <Eye size={24} />
              </div>
              <div>
                <p className="text-[12px] font-semibold uppercase tracking-wider text-ink-400">Total views</p>
                <p className="font-display text-3xl font-bold text-ink-800">{viewCount}</p>
                <p className="text-[12px] text-ink-400">people viewed your profile</p>
              </div>
            </div>
          </div>

          {/* Premium: show viewer list */}
          {premium ? (
            <div className="mt-4 space-y-2">
              <p className="section-subtle px-1">Recent viewers</p>
              {views.slice(0, 20).map((view) => {
                const viewer = viewProfiles[view.viewerId];
                const photo = getProfileDisplayImage(viewer);
                return (
                  <Link
                    key={view.id}
                    to={viewer ? createProfilePath(viewer) : "#"}
                    className="card flex items-center gap-3 p-3 transition hover:shadow-elevated"
                  >
                    <img
                      src={photo}
                      alt={viewer?.fullName || "Karanki Member"}
                      className="h-14 w-14 rounded-2xl object-cover object-center ring-1 ring-sand-200"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <p className="truncate font-display text-[15px] font-bold text-ink-800">
                          {viewer?.fullName || "Karanki Member"}
                        </p>
                        {viewer?.kmProfileId && (
                          <span className="shrink-0 rounded-full bg-sand-100 px-2 py-0.5 text-[10px] font-bold text-ink-500">{viewer.kmProfileId}</span>
                        )}
                      </div>
                      <p className="mt-0.5 text-[12px] text-ink-400">
                        {[viewer?.profession, viewer?.presentResidence || viewer?.state].filter(Boolean).join(" • ") || "—"}
                      </p>
                    </div>
                    <span className="shrink-0 rounded-full bg-sand-50 px-2.5 py-1 text-[11px] font-semibold text-ink-400">
                      {formatViewTime(view.viewedAt)}
                    </span>
                  </Link>
                );
              })}
            </div>
          ) : (
            /* Free: show count + upgrade prompt */
            <div className="mt-4 card p-5 sm:p-6 text-center">
              <div className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-50 text-amber-600">
                <Lock size={22} />
              </div>
              <h3 className="mt-3 font-display text-lg font-bold text-ink-800">
                {viewCount} {viewCount === 1 ? "person" : "people"} viewed your profile
              </h3>
              <p className="mt-1.5 text-[13px] text-ink-500">
                Upgrade to Premium to see who's interested in you.
              </p>
              <div className="mt-4 grid grid-cols-3 gap-2">
                {views.slice(0, 3).map((view) => (
                  <div key={view.id} className="flex flex-col items-center gap-1">
                    <div className="h-12 w-12 rounded-full bg-sand-200 blur-[6px]" />
                    <span className="text-[10px] text-ink-300">Hidden</span>
                  </div>
                ))}
              </div>
              <Link to="/settings/membership" className="btn-primary mt-5 w-full py-3">
                <Crown size={15} /> Upgrade to see viewers
              </Link>
            </div>
          )}
        </>
      )}
    </SettingsLayout>
  );
}
